import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardBody, CardFooter, Dialog, Input, Typography } from "@material-tailwind/react";
import Label from "components/Label/Label";
import { CreatorSchemaType, creatorSchema } from "data/validation";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import ButtonSecondary from "shared/Button/ButtonSecondary";

// Define field configuration type
interface FieldConfig {
  name: keyof CreatorSchemaType;
  label: string;
  type: "text" | "email" | "password";
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

// Define props interface
interface CreatorEntityDialogFormProps {
  open: boolean;
  handleClose: () => void;
  onSubmitSuccess?: () => void;
  id?: string | number;
  defaultValues?: Partial<CreatorSchemaType>;
  fields?: FieldConfig[];
  addfn?: (payload: CreatorSchemaType) => Promise<any>;
  updatefn?: (payload: Partial<CreatorSchemaType>) => Promise<any>;
  title?: {
    add: string;
    update: string;
  };
  isLoading?: boolean;
}

// Default field configuration
const defaultFields: FieldConfig[] = [
  {
    name: "email",
    label: "Email",
    type: "email",
    placeholder: "exemple@mail.com",
    required: true,
  },
  {
    name: "login",
    label: "Pseudo",
    type: "text",
    placeholder: "pseudo",
    required: true,
  },
  {
    name: "password",
    label: "Mot de passe",
    type: "password",
    placeholder: "mot de passe",
    required: true,
  },
];

// Password requirements configuration
const passwordRequirementsConfig = [
  { key: "minLength", label: "8 caractères minimum", test: (value: string) => value.length >= 8 },
  { key: "hasLowercase", label: "1 minuscule", test: (value: string) => /[a-z]/.test(value) },
  { key: "hasUppercase", label: "1 majuscule", test: (value: string) => /[A-Z]/.test(value) },
  { key: "hasNumber", label: "1 chiffre", test: (value: string) => /[0-9]/.test(value) },
  {
    key: "hasSpecialChar",
    label: "1 caractère spécial",
    test: (value: string) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?À-ÿ]/.test(value),
  },
];

export const CreatorEntityDialogForm: React.FC<CreatorEntityDialogFormProps> = ({
  open,
  handleClose,
  onSubmitSuccess,
  id,
  defaultValues = {},
  fields = defaultFields,
  addfn,
  updatefn,
  title = { add: "Ajouter rédacteur", update: "Mettre à jour le mot de passe du rédacteur" },
  isLoading = false,
}) => {
  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreatorSchemaType>({
    resolver: zodResolver(creatorSchema),
    defaultValues: {
      email: "",
      login: "",
      password: "",
      ...defaultValues,
    },
  });

  const [showPassword, setShowPassword] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState(
    passwordRequirementsConfig.reduce((acc, req) => ({ ...acc, [req.key]: false }), {} as Record<string, boolean>)
  );
  const [loading, setLoading] = useState(false);

  // Reset form when dialog opens/closes or id changes
  useEffect(() => {
    if (open) {
      reset({ email: "", login: "", password: "", ...defaultValues });
    }
  }, [open, defaultValues, reset]);

  // Update password requirements
  useEffect(() => {
    const password = watch("password") || "";
    const updatedRequirements = passwordRequirementsConfig.reduce(
      (acc, req) => ({ ...acc, [req.key]: req.test(password) }),
      {} as Record<string, boolean>
    );
    setPasswordRequirements(updatedRequirements);
  }, [watch("password")]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const onSubmit = async (data: CreatorSchemaType) => {
    try {
      setLoading(true);
      let response;

      if (id && updatefn) {
        response = await updatefn({ password: data.password });
        if (response?.payload?.status === "OK") {
          toast.success("Rédacteur mis à jour avec succès");
        } else {
          throw new Error(response?.payload?.error?.[0]?.msg || "Échec de la mise à jour");
        }
      } else if (addfn) {
        response = await addfn(data);
        if (response?.payload?.status === "OK") {
          toast.success("Rédacteur ajouté avec succès");
        } else {
          throw new Error(response?.payload?.error?.[0]?.msg || "Échec de l'ajout");
        }
      }

      onSubmitSuccess?.();
      handleClose();
      reset();
    } catch (error: any) {
      toast.error(error.message || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      size="sm"
      open={open}
      handler={handleClose}
      className="w-full max-w-2xl mx-auto transition-all duration-300 bg-white shadow-2xl rounded-2xl dark:bg-gray-900"
      aria-labelledby="dialog-title"
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Card className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl">
          <CardBody className="flex flex-col gap-6 p-6 max-h-[70vh] overflow-y-auto bg-white dark:bg-gray-900 rounded-t-2xl">
            <Typography
              id="dialog-title"
              variant="h5"
              className="p-4 font-bold tracking-tight text-center text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl animate-fade-in"
            >
              {id ? title.update : title.add}
            </Typography>

            {fields.map((field) => (
              <div key={field.name} className="relative">
                <Label className="font-semibold text-gray-700 dark:text-gray-200">{field.label} :</Label>
                <div className="relative mt-2">
                  <Input
                    size="lg"
                    type={field.type === "password" && showPassword ? "text" : field.type}
                    placeholder={id && field.disabled ? "" : field.placeholder}
                    disabled={!!id && field.disabled}
                    {...register(field.name)}
                    aria-invalid={!!errors[field.name]}
                    aria-describedby={`${field.name}-error`}
                    className="pr-12 text-gray-900 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 dark:text-white hover:shadow-md focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                  />
                  {field.type === "password" && (
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute text-gray-500 -translate-y-1/2 right-3 top-1/2 hover:text-gray-700 dark:hover:text-gray-300"
                      aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    >
                      {showPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
                    </button>
                  )}
                </div>
                {errors[field.name] && (
                  <Typography
                    id={`${field.name}-error`}
                    variant="small"
                    color="red"
                    className="mt-1 text-xs text-red-500 dark:text-red-400"
                  >
                    {errors[field.name]?.message}
                  </Typography>
                )}
              </div>
            ))}

            {fields.some((f) => f.name === "password") && (
              <div className="grid grid-cols-1 gap-2 p-3 rounded-lg shadow-sm sm:grid-cols-2 bg-gray-50 dark:bg-gray-800">
                {passwordRequirementsConfig.map((req) => (
                  <div key={req.key} className="flex items-center">
                    {passwordRequirements[req.key] ? (
                      <CheckIcon className="w-4 h-4 mr-1 text-green-500 dark:text-green-400" />
                    ) : (
                      <XMarkIcon className="w-4 h-4 mr-1 text-red-500 dark:text-red-400" />
                    )}
                    <Typography
                      variant="small"
                      className={`text-sm ${
                        passwordRequirements[req.key]
                          ? "text-green-500 dark:text-green-400"
                          : "text-red-500 dark:text-red-400"
                      }`}
                    >
                      {req.label}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
          <CardFooter className="flex justify-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
            <ButtonSecondary
              type="button"
              onClick={handleClose}
              className="px-6 py-2 text-gray-900 transition-colors duration-200 bg-gray-200 rounded-full dark:bg-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600"
              disabled={loading || isLoading}
            >
              Annuler
            </ButtonSecondary>
            <ButtonSecondary
              type="submit"
              className="px-6 py-2 text-white transition-all duration-300 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 hover:from-blue-600 hover:to-indigo-700 dark:hover:from-blue-700 dark:hover:to-indigo-800 hover:shadow-lg"
              disabled={loading || isLoading}
              loading={loading || isLoading}
            >
              {id ? "Mettre à jour" : "Confirmer"}
            </ButtonSecondary>
          </CardFooter>
        </Card>
      </form>
    </Dialog>
  );
};
